import React from 'react';
import {
  Drawer, Box, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Divider, Typography, Tooltip
} from '@mui/material';
import {
  Dashboard, People, Business, FolderSpecial, Assessment,
  Category, AccessTime, Group, AutoAwesome, BeachAccess
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { INDESAP } from '../../theme';

const NAV_ITEMS = {
  superadmin: [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
    { divider: true },
    { text: 'Usuarios', icon: <People />, path: '/admin/users' },
    { text: 'Clientes', icon: <Business />, path: '/admin/clients' },
    { text: 'Categorías', icon: <Category />, path: '/admin/categories' },
    { text: 'Días Festivos', icon: <BeachAccess />, path: '/admin/holidays' },
  ],
  teamlead: [
    { text: 'Dashboard', icon: <Dashboard />, path: '/teamlead/dashboard' },
    { divider: true },
    { text: 'Clientes', icon: <Business />, path: '/teamlead/clients' },
    { text: 'Proyectos', icon: <FolderSpecial />, path: '/teamlead/projects' },
    { text: 'Equipos', icon: <Group />, path: '/teamlead/team' },
    { text: 'Actividades', icon: <AccessTime />, path: '/teamlead/activities' },
    { text: 'Reportes', icon: <Assessment />, path: '/teamlead/reports' },
    { text: 'Análisis IA', icon: <AutoAwesome />, path: '/teamlead/ai-analysis' },
  ],
  member: [
    { text: 'Inicio', icon: <Dashboard />, path: '/member/dashboard' },
    { divider: true },
    { text: 'Mis Proyectos', icon: <FolderSpecial />, path: '/member/projects' },
    { text: 'Registro de Horas', icon: <AccessTime />, path: '/member/activities' },
  ],
  clientlead: [
    { text: 'Dashboard', icon: <Dashboard />, path: '/clientlead/dashboard' },
    { divider: true },
    { text: 'Actividades', icon: <AccessTime />, path: '/clientlead/activities' },
    { text: 'Reportes', icon: <Assessment />, path: '/clientlead/reports' },
    { text: 'Análisis IA', icon: <AutoAwesome />, path: '/clientlead/ai-analysis' },
  ]
};

const DrawerContent = ({ onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const items = NAV_ITEMS[user?.role] || [];

  const handleNav = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{
          color: 'white', fontWeight: 800, letterSpacing: '0.15em',
          textShadow: `0 0 20px ${INDESAP.cyan}88`
        }}>
          INDESAP
        </Typography>
        <Typography sx={{
          color: INDESAP.cyan, fontSize: '0.6rem', letterSpacing: '0.25em',
          mt: 0.5, textTransform: 'uppercase', fontWeight: 300
        }}>
          Gestor de Actividades
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Nav items */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {items.map((item, idx) => {
          if (item.divider) return (
            <Divider key={idx} sx={{ my: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
          );

          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => handleNav(item.path)}
                sx={{
                  mx: 0.5, borderRadius: 1, py: 1.2,
                  '& .MuiListItemIcon-root': { color: isActive ? INDESAP.cyan : 'rgba(255,255,255,0.7)', minWidth: 40 },
                  '& .MuiListItemText-primary': {
                    color: isActive ? 'white' : 'rgba(255,255,255,0.8)',
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.9rem'
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
                {isActive && (
                  <Box sx={{ width: 4, height: 4, bgcolor: INDESAP.cyan, borderRadius: '50%' }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* User info at bottom */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ px: 2, py: 2 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.4 }}>
          Conectado como
        </Typography>
        <Tooltip title={user?.email || ''} placement="top">
          <Typography variant="body2" sx={{
            color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontSize: '0.8rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {user?.name ? `${user.name} ${user.lastName || ''}` : user?.email || ''}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default function Sidebar({ drawerWidth, mobileOpen, onClose, isMobile }) {
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: drawerWidth } }}
      >
        <DrawerContent onClose={onClose} />
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', border: 'none' }
      }}
    >
      <DrawerContent />
    </Drawer>
  );
}
